import type { ReactNode } from "react"

import { cn } from "../../lib/utils"

/*
 * GuestProfileFanin — the Stampede homepage hero diagram, as a shared block.
 *
 * Guest signals stream in along live gradient beams and resolve into ONE owned
 * guest-profile card. This is a port of the wir homepage hero (`HeroFanin`),
 * lifted here so the accounts sign-in and anything else that needs the picture
 * uses the SAME diagram rather than a second drawing of the same claim. The
 * accounts pane had its own pure-SVG version: same idea, different card, boxed
 * in a blueprint frame with `FIG.01` plate numbers the design law does not use,
 * and beams with no flow in them.
 *
 * Self-contained on purpose. wir's copy leans on app-level CSS (`.hf-beam-d`,
 * `.live-flash`, `.skel-shine`) and on `--brand-green`, none of which exist in
 * this package. The keyframes it needs live in the package stylesheet instead,
 * `gpf-` prefixed (search `GUEST-PROFILE FAN-IN` in styles/globals.css), and the
 * live dot uses `--success`, the status colour the law names for a live mark.
 * They were briefly a `<style>` tag this component rendered, which SSR'd as
 * different text than it re-rendered and tripped a hydration mismatch, and which
 * emitted itself several times per diagram. Colour otherwise comes from
 * `--brand-gradient` and the semantic tokens, so it themes with its host: the
 * accounts brand pane follows the visitor's theme and the diagram follows it.
 *
 * The rules it is built to, worth keeping if you edit it:
 *
 *  • GRADIENT IS RATIONED. Wires are neutral `--border` hairlines. The accent is
 *    confined to one travelling packet per wire, the signal dots, the avatar and
 *    the single active record row. A fully coloured fan is the classic mistake.
 *  • BEAMS FLOW LEFT TO RIGHT. The animation drives `stroke-dashoffset` upward,
 *    which walks each packet toward the path's START, so every beam is drawn
 *    DESTINATION-FIRST: `M`(hub, on the right) and ending at the source dot on
 *    the left. Draw one source-first and it runs backwards.
 *  • ONE LOOPING MOTION, and it is the beam. Everything else is painted on load.
 *    `prefers-reduced-motion` freezes the lot.
 *  • GEOMETRY IS DERIVED, NOT EYEBALLED. `rowY()` places the labels, their dots
 *    and the beam ends off one number, and the beams converge on a point that IS
 *    the card's left-edge centre.
 */

/* Per-line beam timing. Irrational-ish steps so no two wires ever sync up. */
export const beamDur = (i: number) => 1.7 + ((i * 0.61 + 0.13) % 1.6)
export const beamPhase = (i: number, d: number) => (i * 0.83 + i * i * 0.19) % d

const GRADIENT_STOPS = (
  <>
    {/* Raw hex, the one place the law allows it: Safari will not resolve a
        var() inside an SVG <stop>. These equal --brand-hot / --brand-warm /
        --brand-glow. The coral sits at 55%, not 50%, deliberately. */}
    <stop offset="0%" stopColor="#ff2d87" />
    <stop offset="55%" stopColor="#ff6b4a" />
    <stop offset="100%" stopColor="#ffa234" />
  </>
)

export type GuestProfileCardProps = {
  className?: string
  /**
   * Which record rows are active. Length drives the card's height, so a taller
   * frame gets more rows rather than a stretched card. Exactly one true is the
   * intent: the gradient marks the ONE live thing.
   */
  rows?: boolean[]
  /** Opens the card up on-scale, for filling a taller frame. */
  roomy?: boolean
  /**
   * `background` sits the card a shade darker than a raised `popover` panel, for
   * when it is nested inside one.
   */
  surface?: "popover" | "background"
  /** Sweep a sheen across the placeholder bars. */
  shine?: boolean
  /** Hung below the card, out of flow, so the CARD is what centres on the hub. */
  caption?: ReactNode
  floatCaption?: boolean
}

/**
 * The payoff: one owned guest profile. A FILLED card, never an outlined
 * wireframe of one. Soft 12px radius, hairline border, no shadow, with the
 * gradient spent on exactly two things (the avatar and the single active row).
 */
export function GuestProfileCard({
  className,
  rows = [false, true, false],
  roomy = false,
  surface = "popover",
  shine = false,
  caption,
  floatCaption = false,
}: GuestProfileCardProps) {
  const stackGap = roomy ? "gap-6" : "gap-4"
  const rowsGap = roomy ? "gap-4" : "gap-3"
  const cardPad = roomy ? "p-6" : "p-4"
  const avatar = roomy ? "h-14 w-14" : "h-12 w-12"
  const square = roomy ? "h-8 w-8" : "h-6 w-6"
  const shineCls = shine ? "gpf-shine" : ""

  return (
    <div
      className={cn("relative flex flex-col items-center", stackGap, className)}
    >
      <div
        className={cn(
          "flex w-full flex-col rounded-xl border border-border",
          stackGap,
          cardPad,
          surface === "background" ? "bg-background" : "bg-popover"
        )}
      >
        <div className="flex items-center justify-between">
          <span className="font-sans text-[11px] font-bold uppercase tracking-[0.16em] text-muted-foreground">
            One profile
          </span>
          {/* Live, so --success. Never the brand accent, which is spoken for. */}
          <span
            aria-hidden
            className="gpf-live h-2 w-2 rounded-full bg-success"
          />
        </div>

        <div className="flex items-center gap-3">
          <span
            aria-hidden
            className={cn(avatar, "shrink-0 rounded-full")}
            style={{ background: "var(--brand-gradient)" }}
          />
          <div className="flex flex-1 flex-col gap-2">
            <span
              aria-hidden
              className={cn("h-2 w-[78%] rounded-full", shineCls)}
              style={{
                background:
                  "color-mix(in oklab, var(--foreground) 22%, transparent)",
                ["--gpf-sd" as string]: "0.1s",
              }}
            />
            <span
              aria-hidden
              className={cn("h-2 w-[52%] rounded-full", shineCls)}
              style={{
                background:
                  "color-mix(in oklab, var(--foreground) 12%, transparent)",
                ["--gpf-sd" as string]: "0.2s",
              }}
            />
          </div>
        </div>

        <div className="h-px w-full bg-border" />

        <div className={cn("flex flex-col", rowsGap)}>
          {rows.map((on, i) => (
            <div key={i} className="flex items-center gap-3">
              <span
                aria-hidden
                className={cn(square, "shrink-0 rounded-lg")}
                style={
                  on
                    ? { background: "var(--brand-gradient)" }
                    : {
                        background:
                          "color-mix(in oklab, var(--foreground) 16%, transparent)",
                      }
                }
              />
              <span
                aria-hidden
                className={cn("h-2 flex-1 rounded-full", shineCls)}
                style={{
                  background:
                    "color-mix(in oklab, var(--foreground) 13%, transparent)",
                  ["--gpf-sd" as string]: `${(0.3 + i * 0.12).toFixed(2)}s`,
                }}
              />
              <span
                aria-hidden
                className="h-4 w-8 shrink-0 rounded-lg"
                style={
                  on
                    ? { background: "var(--brand-gradient)" }
                    : { border: "1px solid var(--border)" }
                }
              />
            </div>
          ))}
        </div>
      </div>

      {caption ? (
        <div
          className={cn(
            "flex items-center gap-2 font-sans text-sm text-muted-foreground",
            floatCaption && "absolute inset-x-0 top-full justify-center pt-4"
          )}
        >
          {caption}
        </div>
      ) : null}
    </div>
  )
}

/* ── geometry ─────────────────────────────────────────────────────────────── */

const HUB_X = 50
const HUB_Y = 50
const ROW_EDGE_X = 22
const MAX_PITCH = 19
const FAN_SPAN = 76

/* With a custom `hub` (the homepage's GuestStack) the geometry is the wir
   hero's: the card starts at 46% so it gets 54% of the frame, and the fan
   spreads over 84 so eight signals sit as they do on the site. */
const HUB_X_WIDE = 46
const FAN_SPAN_WIDE = 84

/**
 * Row centres, balanced on the hub. At five signals the pitch is exactly the
 * homepage's 19 (rows at 12/31/50/69/88); past five it tightens so the fan still
 * fits the frame instead of running off the top and bottom.
 */
function rowY(count: number, span = FAN_SPAN): number[] {
  const pitch = Math.min(MAX_PITCH, span / Math.max(1, count - 1))
  return Array.from(
    { length: count },
    (_, i) => HUB_Y + pitch * (i - (count - 1) / 2)
  )
}

/** The stampede.ai homepage hero's signals, in its order (Ordering leads, the
 *  main module). The homepage and the accounts sign-in both read this list. */
export const HOMEPAGE_SIGNALS = [
  "Ordering",
  "Bookings",
  "AI phone",
  "Wi-Fi",
  "Payments",
  "Marketing",
  "Reviews",
  "Loyalty",
]

export const DEFAULT_SIGNALS = [
  "Bookings",
  "Wi-Fi",
  "Payments",
  "Reviews",
  "Loyalty",
]

export type GuestProfileFaninProps = {
  /** Signal names down the left. Keep them to one or two words. */
  signals?: string[]
  rows?: boolean[]
  roomy?: boolean
  surface?: "popover" | "background"
  /** Hung under the card. Omitted by default. */
  caption?: ReactNode
  /** Desktop frame height. The fan and hub lay out inside it. */
  heightClassName?: string
  className?: string
  /** Unique per instance, so two on one page cannot share a gradient id. */
  id?: string
  /**
   * Replaces the placeholder card with your own hub, e.g. the homepage's
   * GuestStack, and switches to the homepage hero's wider geometry. `rows`,
   * `roomy`, `surface` and `caption` only shape the placeholder, so they are
   * ignored when this is set.
   */
  hub?: ReactNode
}

/**
 * The whole diagram: signals left, live beams, owned profile right. Horizontal
 * from `md` up; below that it flips to a vertical funnel rather than shrinking
 * the desktop layout into something unreadable.
 */
export function GuestProfileFanin({
  signals = DEFAULT_SIGNALS,
  rows,
  roomy,
  surface,
  caption,
  heightClassName = "h-[420px]",
  className,
  id = "gpf",
  hub,
}: GuestProfileFaninProps = {}) {
  const cardProps = { rows, roomy, surface, shine: true }
  return (
    <div className={className}>
      <div className={cn("hidden md:block", heightClassName)}>
        <FaninDesktop
          signals={signals}
          caption={caption}
          id={id}
          cardProps={cardProps}
          hub={hub}
        />
      </div>
      <div className="md:hidden">
        <FaninMobile
          signals={signals}
          caption={caption}
          id={id}
          cardProps={cardProps}
          hub={hub}
        />
      </div>
    </div>
  )
}

type CardProps = Pick<
  GuestProfileCardProps,
  "rows" | "roomy" | "surface" | "shine"
>

/* ── desktop: signals left, profile right ─────────────────────────────────── */

function FaninDesktop({
  signals,
  caption,
  id,
  cardProps,
  hub,
}: {
  signals: string[]
  caption?: ReactNode
  id: string
  cardProps: CardProps
  hub?: ReactNode
}) {
  const hubX = hub ? HUB_X_WIDE : HUB_X
  const ys = rowY(signals.length, hub ? FAN_SPAN_WIDE : FAN_SPAN)
  /* Destination-first, so the packets travel INTO the hub. See the header note. */
  const path = (y: number) =>
    `M${hubX} ${HUB_Y} C ${hubX - 10} ${HUB_Y}, ${ROW_EDGE_X + 14} ${y}, ${ROW_EDGE_X} ${y}`

  return (
    <div className="pointer-events-none relative flex h-full w-full flex-col">
      <div className="relative flex-1">
        <svg
          className="absolute inset-0 z-0 h-full w-full"
          viewBox="0 0 100 100"
          preserveAspectRatio="none"
          aria-hidden
        >
          <defs>
            {/* userSpaceOnUse, not objectBoundingBox: the middle beam is dead
                straight, so its bbox has zero height and a bbox gradient would
                degenerate to nothing (the beam would simply vanish). */}
            <linearGradient
              id={`${id}-h`}
              gradientUnits="userSpaceOnUse"
              x1={ROW_EDGE_X}
              y1={0}
              x2={hubX}
              y2={0}
            >
              {GRADIENT_STOPS}
            </linearGradient>
          </defs>

          {ys.map((y, i) => (
            <path
              key={`wire-${i}`}
              d={path(y)}
              fill="none"
              stroke="var(--border)"
              strokeWidth={1}
              strokeLinecap="round"
              vectorEffect="non-scaling-stroke"
            />
          ))}
          {ys.map((y, i) => {
            const d = beamDur(i)
            return (
              <path
                key={`beam-${i}`}
                className="gpf-beam-d"
                d={path(y)}
                fill="none"
                stroke={`url(#${id}-h)`}
                strokeWidth={1.5}
                strokeLinecap="round"
                vectorEffect="non-scaling-stroke"
                pathLength={100}
                style={{
                  ["--gpf-dur" as string]: `${d.toFixed(2)}s`,
                  animationDelay: `-${beamPhase(i, d).toFixed(2)}s`,
                }}
              />
            )
          })}
        </svg>

        {/* Dots pinned to the beam tails, off the same ys as the labels. */}
        <div aria-hidden className="pointer-events-none absolute inset-0 z-[1]">
          {ys.map((y, i) => (
            <span
              key={`dot-${i}`}
              className="absolute h-1.5 w-1.5 -translate-x-1/2 -translate-y-1/2 rounded-full"
              style={{
                left: `${ROW_EDGE_X}%`,
                top: `${y}%`,
                background: "var(--brand-gradient)",
              }}
            />
          ))}
        </div>

        <div className="relative z-[1] h-full w-[46%]">
          {signals.map((s, i) => (
            <span
              key={s}
              className="absolute left-0 -translate-y-1/2 px-4 font-sans text-[15px] font-semibold leading-none tracking-[-0.01em] text-foreground"
              style={{ top: `${ys[i]}%` }}
            >
              {s}
            </span>
          ))}
        </div>

        {/* `left` is the hub x: the beams land on this card's left-edge centre. */}
        <div
          className="absolute right-0 top-1/2 z-[1] -translate-y-1/2"
          style={{ left: `${hubX}%` }}
        >
          {hub ?? (
            <GuestProfileCard {...cardProps} caption={caption} floatCaption />
          )}
        </div>
      </div>
    </div>
  )
}

/* ── mobile: a vertical funnel ────────────────────────────────────────────── */

function FaninMobile({
  signals,
  caption,
  id,
  cardProps,
  hub,
}: {
  signals: string[]
  caption?: ReactNode
  id: string
  cardProps: CardProps
  hub?: ReactNode
}) {
  const xs = signals.map((_, i) => ((i + 0.5) / signals.length) * 100)
  const path = (x: number) => `M50 100 C50 45, ${x} 55, ${x} 0`

  return (
    <div className="relative flex w-full flex-col">
      <div className="flex items-start justify-between gap-1 border-b border-border px-3 pb-3 pt-4">
        {signals.map((s) => (
          <div key={s} className="flex flex-1 flex-col items-center gap-2">
            <span
              aria-hidden
              className="h-1.5 w-1.5 rounded-full"
              style={{ background: "var(--brand-gradient)" }}
            />
            <span className="text-center font-sans text-[11px] uppercase leading-tight tracking-[0.04em] text-muted-foreground">
              {s}
            </span>
          </div>
        ))}
      </div>

      <div className="relative h-20">
        <svg
          className="absolute inset-0 h-full w-full"
          viewBox="0 0 100 100"
          preserveAspectRatio="none"
          aria-hidden
        >
          <defs>
            <linearGradient
              id={`${id}-v`}
              gradientUnits="userSpaceOnUse"
              x1={50}
              y1={0}
              x2={50}
              y2={100}
            >
              {GRADIENT_STOPS}
            </linearGradient>
          </defs>

          {xs.map((x, i) => (
            <path
              key={`wire-${i}`}
              d={path(x)}
              fill="none"
              stroke="var(--border)"
              strokeWidth={1}
              strokeLinecap="round"
              vectorEffect="non-scaling-stroke"
            />
          ))}
          {xs.map((x, i) => {
            const d = beamDur(i)
            return (
              <path
                key={`beam-${i}`}
                className="gpf-beam"
                d={path(x)}
                fill="none"
                stroke={`url(#${id}-v)`}
                strokeWidth={1.5}
                strokeLinecap="round"
                vectorEffect="non-scaling-stroke"
                pathLength={100}
                style={{
                  ["--gpf-dur" as string]: `${d.toFixed(2)}s`,
                  animationDelay: `-${beamPhase(i, d).toFixed(2)}s`,
                }}
              />
            )
          })}
        </svg>
      </div>

      <div className="flex flex-col items-center px-4 pb-4">
        {hub ? (
          <div className="w-full max-w-[280px]">{hub}</div>
        ) : (
          <GuestProfileCard
            {...cardProps}
            caption={caption}
            className="w-full max-w-[240px]"
          />
        )}
      </div>
    </div>
  )
}
